import cx from 'classnames';
import { getCookie } from 'cookies-next';
import { useEffect, useRef, useState } from 'react';
import styles from './selectAutocompliteAddress.module.css';

const url = 'https://adm.city-g.ru/api/v1/search';

export type AddressOption = {
  value: string;
  label: string;
  lat: number;
  lon: number;
  data?: Record<string, unknown>;
} | null;

type SelectAddressAutocompliteProps = {
  value?: AddressOption;
  onChange?: (value: AddressOption) => void;
  className?: string;
  isInvalid?: boolean;
  defaultValue?: AddressOption | string;
  placeholder?: string;
};

type SuggestionType = {
  unrestricted_value: string;
  data: {
    geo_lat: number;
    geo_lon: number;
  };
};

const SelectAddressAutocomplite: React.FC<SelectAddressAutocompliteProps> = ({
  value,
  onChange,
  defaultValue,
  className = '',
  isInvalid = false,
  placeholder = 'Укажите адрес',
}) => {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState<SuggestionType[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLUListElement>(null);
  const token = getCookie('cg-token');

  // Initialize with default value if provided
  useEffect(() => {
    if (defaultValue) {
      if (typeof defaultValue === 'string') {
        setInputValue(defaultValue);
      } else {
        setInputValue(defaultValue.value);
      }
    }
  }, [defaultValue]);

  // Update input value when external value changes
  useEffect(() => {
    if (value) {
      setInputValue(value.value);
    }
  }, [value]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchSuggestions = async (query: string) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        Authorization: `Token ${token}`,
      },
      body: JSON.stringify({ nolimits: true, s: query }),
    };

    try {
      const response = await fetch(url, requestOptions);
      const data = await response.json();
      setSuggestions(data.suggestions || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Ошибка:', error);
      setSuggestions([]);
    }
  };

  // Debounce function to limit API calls
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  const debounce = (func: Function, delay: number) => {
    let timeoutId: NodeJS.Timeout;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (...args: any[]) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  // Create debounced version of fetchSuggestions
  const debouncedFetchSuggestions = useRef(
    debounce(fetchSuggestions, 300),
  ).current;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Update the parent component with the current input value
    const addressOption: AddressOption = {
      value: newValue,
      label: newValue,
      lat: 0,
      lon: 0,
    };
    onChange?.(addressOption);

    // Fetch suggestions for the new input value
    debouncedFetchSuggestions(newValue);
  };

  const handleSuggestionClick = (suggestion: SuggestionType) => {
    const addressValue = suggestion.unrestricted_value;
    setInputValue(addressValue);

    // Update the parent component with the selected suggestion
    const addressOption: AddressOption = {
      value: addressValue,
      label: addressValue,
      lat: suggestion.data.geo_lat,
      lon: suggestion.data.geo_lon,
      ...suggestion,
    };
    onChange?.(addressOption);

    setShowSuggestions(false);
  };

  const handleInputFocus = () => {
    if (inputValue.length >= 2) {
      debouncedFetchSuggestions(inputValue);
    }
  };

  return (
    <div className={cx(styles.autocompleteContainer, className)}>
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={placeholder}
        className={cx(styles.autocompleteInput, isInvalid ? styles.error : '')}
      />

      {showSuggestions && suggestions.length > 0 && (
        <ul ref={suggestionsRef} className={styles.suggestions}>
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              className={styles.suggestionItem}
            >
              {suggestion.unrestricted_value}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SelectAddressAutocomplite;
